/**
 * @fileoverview Embedded Stripe Checkout for a one-time AI-minute top-up (I6).
 *
 * The sibling of `StripeCheckout`, and separate from it for the same reason
 * the endpoints are: almost nothing about the two is the same transaction.
 * This one buys a fixed quantity of minutes once, so there is no plan to
 * choose and no billing cycle to pick — the whole screen is a single figure
 * and a confirm. A tier selector bolted onto it would be three controls where
 * a purchase needs none.
 *
 * It posts to `/subscription/stripe/create-topup-session`, which — unlike the
 * subscription checkout — deliberately does NOT refuse an existing subscriber.
 * They are the people who run out.
 */

import { useState, useCallback } from "react";
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from "@stripe/react-stripe-js";
import { X, ArrowRight, Zap } from "lucide-react";
import api from "@/lib/api";
import CheckoutUnavailable from "@/components/CheckoutUnavailable";
import SubscriptionComingSoon from "@/components/SubscriptionComingSoon";
import { getStripe, stripeConfigured } from "@/lib/stripeClient";
import { SUBSCRIPTION_PRICING_VISIBLE } from "@/lib/featureFlags";
import {
  TOPUP_MINUTES,
  TOPUP_PLAN_ALIAS,
  TOPUP_PRICE_CENTS,
  formatPriceFromCents,
  topupHours,
} from "@/lib/pricing";

/**
 * @param {object} props
 * @param {function} props.onClose - Callback to close the checkout UI.
 * @param {boolean} [props.showHeader=true] - Draw the title + close control.
 *   Pass false when a dialog already provides them.
 * @returns {React.ReactElement}
 */
const TopUpCheckout = ({ onClose, showHeader = true }) => {
  const [step, setStep] = useState("select"); // "select" | "checkout" | "error"
  const [error, setError] = useState(null);

  const fetchClientSecret = useCallback(async () => {
    try {
      const res = await api.post("/subscription/stripe/create-topup-session", {
        price_id: TOPUP_PLAN_ALIAS,
        return_url: `${window.location.origin}/checkout/return`,
      });
      return res.data.client_secret;
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError((typeof detail === "string" ? detail : detail?.message) || "Failed to start checkout.");
      setStep("error");
      throw err;
    }
  }, []);

  // Inline on Settings the body draws its own title + close; inside a dialog
  // the dialog owns that chrome, and two close controls in one corner is what
  // a second header produces.
  const header = (title) => showHeader && (
    <div className="mb-6 flex items-center justify-between">
      <h3 className="font-serif font-medium text-[var(--text-primary)]">{title}</h3>
      <button
        onClick={onClose}
        aria-label="Close"
        className="text-[var(--text-tertiary)] transition-colors hover:text-[var(--text-primary)]"
      >
        <X className="h-5 w-5" strokeWidth={1.5} />
      </button>
    </div>
  );

  // Same rule as the subscription paywall: this is where a charge is actually
  // raised, so a price that is being replaced must not be buyable. Note a
  // top-up rides its OWN Stripe price (STRIPE_PRICE_ID_TOPUP_600), not one of
  // the ladder's — so this surface can be visible while that price is still
  // unset, in which case the endpoint 503s rather than 400s.
  if (!SUBSCRIPTION_PRICING_VISIBLE) {
    return (
      <div>
        {header("Add recording minutes")}
        <SubscriptionComingSoon note="Your current minutes are unchanged." />
      </div>
    );
  }

  if (step === "error") {
    return (
      <div>
        {header("Add recording minutes")}
        <p className="text-sm text-[var(--state-danger)]">{error}</p>
        <button
          onClick={() => {
            setError(null);
            setStep("select");
          }}
          className="mt-4 text-sm text-[var(--text-secondary)] transition-colors hover:text-[var(--text-primary)]"
        >
          Try again
        </button>
      </div>
    );
  }

  if (step === "select") {
    return (
      <div>
        {header("Add recording minutes")}

        <div className="rounded-xl border border-[var(--accent-yellow)]/20 bg-[var(--accent-yellow)]/5 p-6">
          <div className="flex items-center gap-3">
            <Zap className="h-5 w-5 text-[var(--accent-yellow-text)]" strokeWidth={1.5} />
            <div className="flex-1">
              <p className="font-mono text-2xl text-[var(--text-primary)]">{TOPUP_MINUTES} minutes</p>
              <p className="text-xs text-[var(--text-tertiary)]">
                About {topupHours()} hours of AI lesson recording
              </p>
            </div>
            <p className="font-serif text-2xl text-[var(--text-primary)]">
              {formatPriceFromCents(TOPUP_PRICE_CENTS)}
            </p>
          </div>
        </div>

        {/* The two properties a buyer most needs to know, and the two the
            feature was designed around: bought minutes outlive the month, and
            they are spent last so a purchase is never drained while included
            minutes sit unused. */}
        <ul className="mt-4 flex flex-col gap-1.5 text-xs text-[var(--text-secondary)]">
          <li>These minutes never expire.</li>
          <li>Your monthly minutes are always used first.</li>
          <li>One-time purchase — nothing recurring, and nothing renews.</li>
        </ul>

        <button
          onClick={() => setStep("checkout")}
          className="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-[var(--accent-yellow)] px-6 py-3 text-sm font-medium text-[var(--text-inverse)] transition-transform active:scale-95"
        >
          Continue to Payment <ArrowRight className="h-4 w-4" strokeWidth={1.5} />
        </button>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={() => setStep("select")}
            className="text-sm text-[var(--text-tertiary)] transition-colors hover:text-[var(--text-primary)]"
          >
            &larr; Back
          </button>
          <h3 className="font-serif font-medium text-[var(--text-primary)]">Complete Payment</h3>
        </div>
        <button
          onClick={onClose}
          aria-label="Close"
          className="text-[var(--text-tertiary)] transition-colors hover:text-[var(--text-primary)]"
        >
          <X className="h-5 w-5" strokeWidth={1.5} />
        </button>
      </div>
      {/* See the note in StripeCheckout: a null `stripe` prop renders an empty
          div and nothing else, so an unconfigured build has to say so. */}
      {stripeConfigured() ? (
        <EmbeddedCheckoutProvider stripe={getStripe()} options={{ fetchClientSecret }}>
          <EmbeddedCheckout />
        </EmbeddedCheckoutProvider>
      ) : (
        <CheckoutUnavailable note="Your current minutes are unchanged." />
      )}
    </div>
  );
};

export default TopUpCheckout;
